import React, { useState } from 'react';
import { Container, Form, Button, ProgressBar, Row, Col, Nav, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import Select from 'react-select';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
`;

const AppNavbar = styled(Navbar)`
  background-color: var(--primary);
  padding: 0.75rem 1.5rem;
  color: white;
`;

const NavLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
  color: white;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 2rem;
`;

const FormCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const StyledForm = styled(Form)`
  margin-top: 1rem;
`;

const ActionButton = styled(Button)`
  margin-right: 1rem;
  padding: 0.5rem 1.5rem;
`;

const targetLangOptions = [
  { value: 'zh-cn', label: '简体中文' },
  { value: 'zh-tw', label: '繁体中文' },
  { value: 'en-us', label: '美式英语' },
  { value: 'ko', label: '韩语' },
  { value: 'ja', label: '日语' },
  { value: 'th', label: '泰语' },
  { value: 'id', label: '印尼语' },
  { value: 'vi', label: '越南语' },
  { value: 'tr', label: '土耳其语' },
  { value: 'it', label: '意大利语' },
  { value: 'de', label: '德语' },
  { value: 'fr', label: '法语' },
  { value: 'ru', label: '俄语' },
  { value: 'es-es', label: '西班牙语' },
  { value: 'es-la', label: '拉美西班牙语' },
  { value: 'pt-pt', label: '葡语' },
  { value: 'pt-br', label: '巴西葡语' },
];

function TestPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [termBase, setTermBase] = useState(null);
  const [memoryBase, setMemoryBase] = useState(null);
  const [sourceLang, setSourceLang] = useState('');
  const [targetLang, setTargetLang] = useState([]);
  const [gameType, setGameType] = useState('');
  const [fileColumns, setFileColumns] = useState([]);
  const [sourceColumn, setSourceColumn] = useState('');
  const [termBaseColumns, setTermBaseColumns] = useState([]);
  const [termBaseSourceColumn, setTermBaseSourceColumn] = useState('');
  const [termBaseTargetColumn, setTermBaseTargetColumn] = useState([]);
  const [memoryBaseColumns, setMemoryBaseColumns] = useState([]);
  const [memoryBaseSourceColumn, setMemoryBaseSourceColumn] = useState('');
  const [memoryBaseTargetColumn, setMemoryBaseTargetColumn] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadColumnsFromFile = (file, setColumns) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      const data = new Uint8Array(arrayBuffer);
      const decoder = new TextDecoder('utf-8');
      const decodedData = decoder.decode(data);

      const workbook = XLSX.read(decodedData, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const columns = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
      setColumns(columns);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    loadColumnsFromFile(selectedFile, setFileColumns);
  };

  const handleTermBaseChange = (e) => {
    const selectedFile = e.target.files[0];
    setTermBase(selectedFile);
    loadColumnsFromFile(selectedFile, setTermBaseColumns);
  };

  const handleMemoryBaseChange = (e) => {
    const selectedFile = e.target.files[0];
    setMemoryBase(selectedFile);
    loadColumnsFromFile(selectedFile, setMemoryBaseColumns);
  };

  const handleTargetLangChange = (selectedOptions) => {
    setTargetLang(selectedOptions ? selectedOptions.map(option => option.value) : []);
  };

  const handleTermBaseTargetChange = (selectedOptions) => {
    setTermBaseTargetColumn(selectedOptions ? selectedOptions.map(option => option.value) : []);
  };

  const handleMemoryBaseTargetChange = (selectedOptions) => {
    setMemoryBaseTargetColumn(selectedOptions ? selectedOptions.map(option => option.value) : []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (termBaseTargetColumn.length !== 0){
      if (targetLang.length !== termBaseTargetColumn.length) {
        setShowError(true);
        setErrorMessage('目标语言数量与术语库目标列数量不匹配');
        return;
      }
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('term_base', termBase);
    formData.append('memory_base', memoryBase);
    formData.append('source_lang', sourceLang);
    formData.append('target_lang', JSON.stringify(targetLang));
    formData.append('term_base_target_column', JSON.stringify(termBaseTargetColumn));
    formData.append('memory_base_target_column', JSON.stringify(memoryBaseTargetColumn));
    formData.append('game_type', gameType);
    formData.append('story', ''); // 空字符串，因为移除了Story字段
    formData.append('source_column', sourceColumn);
    formData.append('term_base_source_column', termBaseSourceColumn);
    formData.append('memory_base_source_column', memoryBaseSourceColumn);
    
    // 设置默认的ChatGPT参数
    const settings = {
      temperature: 0.5,
      top_p: 0.85,
      presence_penalty: 0.2,
      frequency_penalty: 0.3,
      max_tokens: 4096
    };
    formData.append('settings', JSON.stringify(settings));

    try {
      // Add authentication headers
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.post('/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(progress);
        },
        headers: {
          'X-User-Id': userInfo.id,
          'X-Org-Code': userInfo.org_code,
          'X-User-Level': userInfo.user_level
        }
      });
      console.log(response.data);
      // 可以在这里处理成功响应，比如显示成功提示或者下载结果文件
    } catch (error) {
      console.error(error);
      setShowError(true);
      setErrorMessage('上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const openWorkflow = () => {
    navigate('/workflow');
  };

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

  return (
    <PageContainer>
      <AppNavbar expand="lg">
        <Container>
          <NavLogo>游戏本地化翻译系统</NavLogo>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
            <Nav>
              <Nav.Link className="text-white">您好，{userInfo.username || '用户'}</Nav.Link>
              <Button variant="outline-light" size="sm" onClick={handleLogout}>退出</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </AppNavbar>

      <ContentContainer>
        <Container>
          <Row className="mb-4">
            <Col md={6}>
              <h2>翻译测试</h2>
            </Col>
            <Col md={6} className="text-end">
              <ActionButton variant="primary" onClick={openWorkflow}>
                打开工作流设计
              </ActionButton>
            </Col>
          </Row>

          <FormCard>
            {showError && (
              <div className="alert alert-danger">{errorMessage}</div>
            )}
            
            <StyledForm onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>游戏类型</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={gameType}
                      onChange={(e) => setGameType(e.target.value)}
                      placeholder="请输入游戏类型"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>源语言</Form.Label>
                    <Form.Select 
                      value={sourceLang}
                      onChange={(e) => setSourceLang(e.target.value)}
                    >
                      <option value="">请选择源语言</option>
                      <option value="zh-cn">简体中文</option>
                      <option value="zh-tw">繁体中文</option>
                      <option value="en-us">美式英语</option>
                      <option value="ko">韩语</option>
                      <option value="ja">日语</option>
                      <option value="th">泰语</option>
                      <option value="id">印尼语</option>
                      <option value="vi">越南语</option>
                      <option value="tr">土耳其语</option>
                      <option value="it">意大利语</option>
                      <option value="de">德语</option>
                      <option value="fr">法语</option>
                      <option value="ru">俄语</option>
                      <option value="es-es">西班牙语</option>
                      <option value="es-la">拉美西班牙语</option>
                      <option value="pt-pt">葡语</option>
                      <option value="pt-br">巴西葡语</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>目标语言</Form.Label>
                    <Select
                      isMulti
                      options={targetLangOptions}
                      value={targetLangOptions.filter(option => targetLang.includes(option.value))}
                      onChange={handleTargetLangChange}
                      placeholder="请选择目标语言"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>翻译文件</Form.Label>
                    <Form.Control type="file" onChange={handleFileChange} />
                  </Form.Group>
                </Col>
                {fileColumns.length > 0 && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>源文本列</Form.Label>
                      <Form.Select 
                        value={sourceColumn}
                        onChange={(e) => setSourceColumn(e.target.value)}
                      >
                        <option value="">请选择源文本列</option>
                        {fileColumns.map((col, index) => (
                          <option key={index} value={col}>{col}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>术语库（可选）</Form.Label>
                    <Form.Control type="file" onChange={handleTermBaseChange} />
                  </Form.Group>
                </Col>
                {termBase && (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>术语库源列</Form.Label>
                        <Form.Select 
                          value={termBaseSourceColumn}
                          onChange={(e) => setTermBaseSourceColumn(e.target.value)}
                        >
                          <option value="">请选择源列</option>
                          {termBaseColumns.map((col, index) => (
                            <option key={index} value={col}>{col}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>术语库目标列</Form.Label>
                        <Select
                          isMulti
                          options={termBaseColumns.map(col => ({ value: col, label: col }))}
                          value={termBaseColumns.filter(col => termBaseTargetColumn.includes(col)).map(col => ({ value: col, label: col }))}
                          onChange={handleTermBaseTargetChange}
                          placeholder="请选择目标列"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>翻译记忆库（可选）</Form.Label>
                    <Form.Control type="file" onChange={handleMemoryBaseChange} />
                  </Form.Group>
                </Col>
                {memoryBase && (
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>记忆库源列</Form.Label>
                        <Form.Select 
                          value={memoryBaseSourceColumn}
                          onChange={(e) => setMemoryBaseSourceColumn(e.target.value)}
                        >
                          <option value="">请选择源列</option>
                          {memoryBaseColumns.map((col, index) => (
                            <option key={index} value={col}>{col}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>记忆库目标列</Form.Label>
                        <Select
                          isMulti
                          options={memoryBaseColumns.map(col => ({ value: col, label: col }))}
                          value={memoryBaseColumns.filter(col => memoryBaseTargetColumn.includes(col)).map(col => ({ value: col, label: col }))}
                          onChange={handleMemoryBaseTargetChange}
                          placeholder="请选择目标列"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              
              <div className="d-flex mt-4">
                <Button variant="primary" type="submit" disabled={uploading}>
                  {uploading ? '处理中...' : '开始任务'}
                </Button>
                {uploading && (
                  <div className="ms-3 flex-grow-1">
                    <ProgressBar now={progress} label={`${progress}%`} />
                  </div>
                )}
              </div>
            </StyledForm>
          </FormCard>
        </Container>
      </ContentContainer>
    </PageContainer>
  );
}

export default TestPage; 